import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CREATION_STYLE_LABELS } from '@djgg/creations';
import type { GalleryItem } from '../gallery/model.js';
import { slugGradient } from '../gallery/cover.js';
import { compactCount } from './compactCount.js';
import { useDocumentVisible } from '../hooks/useDocumentVisible.js';
import { usePreviewMusic } from './previewMusic.js';

const STORY_SECONDS = 5;
const HERO_PREVIEW_PLAYBACK_SECONDS = 25;
const FEED_PREVIEW_PLAYBACK_SECONDS = 12;
/** Boot windows a demo may miss before the trailer stops asking for it. */
const PREVIEW_BOOT_ATTEMPTS = 3;
type StoryStyle = CSSProperties & {
  '--story-seconds': string;
  '--story-artwork'?: string;
};
type HighlightStyle = CSSProperties & { '--highlight-index': number };


/** Scripted (iframe) previews are allowed only for the checked-in native
 * catalog, same-origin under `/games/<slug>/`. A creation's gameplay preview
 * is always its recorded reel (a video), never a live bundle. */
export function safeIframePreviewUrl(url: string): boolean {
  const parsed = new URL(url, window.location.href);
  return parsed.origin === window.location.origin && /^\/games\/[a-z0-9-]+\//.test(parsed.pathname);
}

/** Select only claims backed by fields already present on the public game row. */
export function promotionalHighlights(item: GalleryItem, now = Date.now()): string[] {
  const highlights: string[] = [];
  if (item.editorsPick?.current) highlights.push("Current Editors' Pick");
  else if (item.editorsPick?.everSelected) highlights.push("Editors' Pick");
  if (item.multiplayer) highlights.push('Multiplayer ready');
  if (item.attachedAssetCount > 0) {
    highlights.push(`${compactCount(item.attachedAssetCount)} original artwork${item.attachedAssetCount === 1 ? '' : 's'}`);
  }
  if (item.plays > 0) highlights.push(`${compactCount(item.plays)} plays`);
  if (item.likes > 0) highlights.push(`${compactCount(item.likes)} likes`);
  if (item.kind === 'creation' && item.forks > 0) highlights.push(`${compactCount(item.forks)} community forks`);
  if (
    item.kind === 'creation' &&
    item.publishedAt !== undefined &&
    now >= item.publishedAt &&
    now - item.publishedAt < 7 * 86_400_000
  ) {
    highlights.push('Freshly released');
  }
  if (highlights.length < 2) highlights.push(`${CREATION_STYLE_LABELS[item.style]} game`);
  if (highlights.length < 2) highlights.push(`Made by @${item.creatorHandle}`);
  return highlights.slice(0, 3);
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

export interface GameMediaStoryProps {
  slug: string;
  title: string;
  coverUrl?: string;
  posterUrl?: string;
  titleOverlayUrl?: string;
  previewUrl?: string;
  previewKind?: 'video' | 'iframe';
  previewProbeUrl?: string;
  assetUrls?: readonly string[];
  highlights?: string[];
  active: boolean;
  variant?: 'feed' | 'hero';
  playHref?: string;
  playLabel?: string;
  onPlay?: () => void;
  testId?: string;
  /** The game's published music loop — the story's soundtrack, heard while
   *  the story is active and the viewer's sound switch is on. `undefined`
   *  while resolving, `null` when the game has none. */
  musicUrl?: string | null;
  /** Controls layered over the media's corner, OUTSIDE the play link (a
   *  button cannot live inside an anchor) — the game page's sound toggle. */
  overlay?: ReactNode;
}

/** Cover → title → proof points → passive gameplay, shared by Explore and GamePage. */
export function GameMediaStory(props: GameMediaStoryProps) {
  // A source replacement owns fresh readiness, timeout and failure state.
  // Never display the new version using a previous iframe's successful probe.
  return <GameMediaStoryContent key={JSON.stringify([props.slug, props.previewUrl, props.previewKind, props.previewProbeUrl])} {...props} />;
}

function GameMediaStoryContent({
  slug,
  title,
  coverUrl,
  posterUrl,
  titleOverlayUrl,
  previewUrl,
  previewKind = 'iframe',
  previewProbeUrl,
  assetUrls = [],
  highlights = [],
  active,
  variant = 'feed',
  playHref,
  playLabel,
  onPlay,
  testId,
  musicUrl,
  overlay,
}: GameMediaStoryProps) {
  const reducedMotion = useReducedMotion();
  const documentVisible = useDocumentVisible();
  usePreviewMusic(musicUrl, active);
  const storyRef = useRef<HTMLDivElement>(null);
  const previewFrameRef = useRef<HTMLIFrameElement>(null);
  const [onscreen, setOnscreen] = useState(true);
  const [previewVerified, setPreviewVerified] = useState(previewKind === 'video' || !previewProbeUrl);
  const [previewReady, setPreviewReady] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewExpired, setPreviewExpired] = useState(false);
  const [bootMisses, setBootMisses] = useState(0);
  const [overlayFailed, setOverlayFailed] = useState(false);
  const artworkUrl = coverUrl || posterUrl;
  const iframeSafe = previewKind !== 'iframe' || (!!previewUrl && safeIframePreviewUrl(previewUrl));
  const showPreview = active && onscreen && documentVisible && !reducedMotion && !!previewUrl && iframeSafe && previewVerified && !previewFailed && !previewExpired;
  const previewPlaybackSeconds = variant === 'hero' ? HERO_PREVIEW_PLAYBACK_SECONDS : FEED_PREVIEW_PLAYBACK_SECONDS;

  useEffect(() => {
    const node = storyRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnscreen(entry?.isIntersecting ?? false),
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setPreviewVerified(previewKind === 'video' || !previewProbeUrl);
    if (previewKind === 'video' || !active || !onscreen || reducedMotion || !previewProbeUrl) return;
    const controller = new AbortController();
    fetch(previewProbeUrl, { method: 'HEAD', credentials: 'omit', signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response.ok) setPreviewVerified(true);
        else setPreviewFailed(true);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if ((error as { name?: string }).name !== 'AbortError') setPreviewFailed(true);
      });
    return () => controller.abort();
  }, [active, onscreen, previewKind, previewProbeUrl, reducedMotion]);

  useEffect(() => {
    setPreviewReady(false);
    setPreviewFailed(false);
    setPreviewExpired(false);
    setBootMisses(0);
  }, [previewUrl]);

  // An iframe/video is recreated after the card leaves its active visible
  // state. Its old readiness belongs to the removed DOM node, so a resumed
  // card must wait for the replacement's real ready event before its clock can
  // begin (and before it earns the long story cycle).
  useEffect(() => {
    if (!showPreview) setPreviewReady(false);
  }, [showPreview]);

  // The trailer LOOPS while it holds the screen: intro (cover → title →
  // highlights) → a gameplay window → intro → gameplay again. Count only
  // rendered gameplay: a slow bundle gets its boot grace, then the full
  // feed/detail window; expiry hands the stage back to the intro, and the next
  // effect re-arms the gameplay one intro cycle later (a fresh mount, so the
  // clip or demo starts from its beginning). Inactive and reduced-motion
  // stories never satisfy this effect's guard; leaving the screen resets the
  // cycle and the boot budget.
  useEffect(() => {
    if (!active) {
      setPreviewExpired(false);
      setBootMisses(0);
      return;
    }
    if (!showPreview || !previewReady) return;
    const timeout = window.setTimeout(
      () => setPreviewExpired(true),
      previewPlaybackSeconds * 1_000,
    );
    return () => window.clearTimeout(timeout);
  }, [active, previewPlaybackSeconds, previewReady, showPreview]);

  useEffect(() => {
    if (previewReady) setBootMisses(0);
  }, [previewReady]);

  useEffect(() => {
    if (!active || !previewExpired) return;
    const timeout = window.setTimeout(() => setPreviewExpired(false), STORY_SECONDS * 1_000);
    return () => window.clearTimeout(timeout);
  }, [active, previewExpired]);

  useEffect(() => setOverlayFailed(false), [titleOverlayUrl]);

  useEffect(() => {
    if (!showPreview || previewReady) return;
    // A slow bundle (a phone booting a demo) may miss its boot window; that is
    // not a broken preview. Hand the stage back to the intro and try again on
    // the next loop — only a real load error, or PREVIEW_BOOT_ATTEMPTS missed
    // windows in a row, retires the gameplay for this activation.
    const timeout = window.setTimeout(() => {
      if (bootMisses + 1 >= PREVIEW_BOOT_ATTEMPTS) setPreviewFailed(true);
      else {
        setBootMisses(bootMisses + 1);
        setPreviewExpired(true);
      }
    }, 15_000);
    return () => window.clearTimeout(timeout);
  }, [bootMisses, previewReady, showPreview]);

  const onIframeLoad = () => {
    if (!previewUrl) return;
    // Natives are same-origin (safeIframePreviewUrl), so they can be
    // inspected: a Hosting/Vite SPA fallback also returns HTTP 200, so `load`
    // alone would advertise the portal's 404 page as gameplay. Give the
    // native boot a beat, then require a game surface.
    window.setTimeout(() => {
      const frame = previewFrameRef.current;
      if (frame?.getAttribute('src') !== previewUrl) return;
      const doc = frame.contentDocument;
      if (doc?.querySelector('canvas, #game-container, [data-djgg-game-root]')) setPreviewReady(true);
      else setPreviewFailed(true);
    }, 800);
  };

  const style: StoryStyle = {
    '--story-seconds': `${showPreview && previewReady ? previewPlaybackSeconds : STORY_SECONDS}s`,
    ...(artworkUrl
      ? { '--story-artwork': `url("${artworkUrl.replaceAll('"', '\\"')}")` }
      : { background: slugGradient(slug) }),
  };

  const media = (
    <div
      className={`game-media-story game-media-story--${variant}${active ? ' is-active' : ''}${
        previewReady && showPreview ? ' is-preview-ready' : ''
      }${previewUrl ? ' has-preview' : ' is-cover-story'}${previewFailed ? ' is-preview-failed' : ''}${
        reducedMotion ? ' is-reduced-motion' : ''
      }`}
      style={style}
      ref={storyRef}
      data-testid={playHref ? undefined : (testId ?? 'game-media-story')}
    >
      <div className="game-media-story__art" aria-hidden="true" />
      {assetUrls.length > 0 && (
        <div className="game-media-story__asset-montage" aria-hidden="true">
          {assetUrls.slice(0, 3).map((url, index) => (
            <img key={url} src={url} alt="" style={{ '--asset-index': index } as CSSProperties} />
          ))}
        </div>
      )}
      {showPreview && previewKind === 'video' && (
        <video
          className="game-media-story__preview"
          src={previewUrl}
          // The still recorded with this clip, so the element shows gameplay
          // instead of a black box while the first frame decodes. `muted` is
          // what makes the autoplay legal and is never conditional — feed and
          // detail playback are silent by policy (ledger row 2.6).
          {...(posterUrl ? { poster: posterUrl } : {})}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          tabIndex={-1}
          aria-hidden="true"
          onLoadedData={() => setPreviewReady(true)}
          onError={() => setPreviewFailed(true)}
          data-testid="game-preview"
        />
      )}
      {showPreview && previewKind === 'iframe' && (
        <iframe
          ref={previewFrameRef}
          className="game-media-story__preview"
          src={previewUrl}
          title={`${title} gameplay preview`}
          sandbox="allow-scripts allow-same-origin"
          allow="autoplay 'none'; camera 'none'; microphone 'none'; geolocation 'none'"
          referrerPolicy="no-referrer"
          tabIndex={-1}
          aria-hidden="true"
          onLoad={onIframeLoad}
          onError={() => setPreviewFailed(true)}
          data-testid="game-preview"
        />
      )}
      <div className="game-media-story__shade" aria-hidden="true" />
      <div className="game-media-story__title" aria-hidden="true">
        {titleOverlayUrl && !overlayFailed ? (
          <img src={titleOverlayUrl} alt="" onError={() => setOverlayFailed(true)} />
        ) : null}
        <span className={titleOverlayUrl && !overlayFailed ? 'game-media-story__fallback-title is-underlay' : 'game-media-story__fallback-title'}>
          {title}
        </span>
      </div>
      {!reducedMotion && highlights.length > 0 && (
        <div className="game-media-story__highlights" aria-hidden="true">
          {highlights.map((highlight, index) => (
            <span key={highlight} style={{ '--highlight-index': index } as HighlightStyle}>
              {highlight}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  let content: ReactNode = media;
  if (playHref) {
    content = (
      <a
        className="game-media-story__play-link"
        href={playHref}
        aria-label={playLabel ?? `Play ${title}`}
        onClick={onPlay}
        data-testid={testId}
      >
        {media}
      </a>
    );
  }
  if (overlay) {
    content = (
      <div className="game-media-story__frame">
        {content}
        {overlay}
      </div>
    );
  }
  return content;
}
